import { FormGroup } from '@angular/forms';
import { AppConstants } from './app.constants';
import { HttpErrorResponse } from '@angular/common/http';

export function numericOnly(event: KeyboardEvent): void {
  const allowedKeys: string[] = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
  const pattern = /[0-9]/;

  if (allowedKeys.includes(event.key) || pattern.test(event.key)) {
    return;
  }

  event.preventDefault();
}

export function onlyCharSingleSpace(event: KeyboardEvent): void {
  const allowedKeys: string[] = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
  const pattern = /^[^\d!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/;

  const value = (event.target as HTMLInputElement).value;
  const lastCharacterIsSpace = value.slice(-1) === ' ';
  const hasSpace = /\s/.test(value);

  if (
    allowedKeys.includes(event.key) ||
    (event.key === ' ' && !hasSpace) ||
    (event.key !== ' ' && pattern.test(event.key)) ||
    (event.key === ' ' && lastCharacterIsSpace)
  ) {
    return;
  }

  event.preventDefault();
}

export function formatDateToYYYYMMDD(dateString: string) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based, so add 1
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
}

export function formatDate(date) {
  if (date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Adding 1 since months are 0-based
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    return date;
  }
}

export function getErrorMessage(
  errorOrMessage: HttpErrorResponse | string,
  statusCode?: number,
): string {
  const ERROR_MESSAGE_MAPPINGS = [
    {
      condition: (message: string) =>
        message ===
        AppConstants.ERROR.CONCURRENT_APPEND_EXCEPTION_MESSAGE.trim().toLowerCase(),
      result: AppConstants.ERROR.END_USER_CONCURRENT_APPEND_EXCEPTION_MESSAGE,
    },
    {
      condition: (message: string) =>
        message.includes(
          'no member added to master eligibility and not sent to scriptcare/tredium with payload',
        ),
      result: AppConstants.ERROR.END_USER_UNABLE_TO_ADD_MEMBER_MESSAGE,
    },
  ];

  const isHttpError = typeof errorOrMessage !== 'string';

  const status = isHttpError ? errorOrMessage?.status : statusCode;

  // Highest priority: timeout errors
  if ([499, 504].includes(status as number)) {
    return AppConstants.ERROR.TIMEOUT_ERROR_MESSAGE;
  }

  const message = isHttpError
    ? (errorOrMessage?.error?.Message?.trim() ?? '')
    : (errorOrMessage?.trim() ?? '');

  if (!message) {
    return AppConstants.ERROR.GENERIC_ERROR_MESSAGE;
  }

  const normalizedMessage = message.toLowerCase();

  const mappedError = ERROR_MESSAGE_MAPPINGS.find(({ condition }) =>
    condition(normalizedMessage),
  );

  if (mappedError) {
    return mappedError.result;
  }

  if (isHttpError) {
    return checkMessageExists(errorOrMessage)
      ? message
      : AppConstants.ERROR.GENERIC_ERROR_MESSAGE;
  }

  return message;
}

function checkMessageExists(err: HttpErrorResponse) {
  return (
    AppConstants.messageArray.includes(err?.error?.Message.trim()) ||
    err?.error?.Message.includes(
      'This member already has an active eligibile coverage with card ID',
    ) ||
    err?.error?.Message.includes(
      AppConstants.ERROR.END_USER_UNABLE_TO_ADD_MEMBER_MESSAGE,
    )
  );
}

export function convertJSONToQueryParams(obj: object): string {
  return Object.entries(obj)
    .filter(
      ([key, value]) => value !== undefined && value !== null && value !== '',
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

export function fromDateToUTCString(date: Date): string {
  const fromUTCDate = date ? new Date(date) : null;
  let fromUTCString = '';
  if (fromUTCDate) {
    fromUTCDate.setUTCHours(0, 0, 0, 0); // Set hours, minutes, seconds, and milliseconds to UTC
    fromUTCDate.setMinutes(fromUTCDate.getTimezoneOffset()); // Adjust for local timezone offset
    fromUTCDate.setDate(fromUTCDate.getDate() + 1); // Add one day to match the day
    fromUTCString = fromUTCDate.toISOString();
    // console.log('Converted fromUTCDate:', fromUTCDate);
    // console.log('Resulting fromUTCString:', fromUTCString);
  }
  return fromUTCString;
}

export function toDateToUTCString(date: Date): string {
  const toUTCDate = date ? new Date(date) : null;
  let toUTCString = '';
  if (toUTCDate) {
    toUTCDate.setUTCHours(23, 59, 59, 999); // Set hours, minutes, seconds, and milliseconds to UTC
    toUTCDate.setMinutes(toUTCDate.getTimezoneOffset()); // Adjust for local timezone offset
    // toUTCDate.setDate(toUTCDate.getDate() + 1); // Add one day to match the day
    toUTCString = toUTCDate.toISOString();
    // console.log('Converted toUTCDate:', toUTCDate);
    // console.log('Resulting toUTCString:', toUTCString);
  }
  return toUTCString;
}

export function formatDateToMMDDYYYY(inputDateString: string): string {
  const dateObject = new Date(inputDateString);

  const month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObject.getDate().toString().padStart(2, '0');
  const year = dateObject.getFullYear();

  const formattedDate = `${month}/${day}/${year}`;

  return formattedDate;
}

export function formatSSN(ssn: string): string {
  if (ssn && ssn.toLowerCase() !== 'null') {
    const regex = /^(\d{3})(\d{2})(\d{4})$/;
    const match = ssn.match(regex);

    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    } else {
      // Handle invalid input (not exactly 9 digits)
      return ssn;
    }
  } else {
    return ssn;
  }
}

export function clearSsnFormat(formattedSsn: string): string {
  if (formattedSsn)
    return formattedSsn.replace(/\D/g, ''); // Remove non-numeric characters
  else return '';
}

export function formatPhone(phone: string): string {
  const regex = /^(\d{3})(\d{3})(\d{4})$/;
  const match = phone.match(regex);

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  } else {
    // Handle invalid input (not exactly 10 digits)
    return phone;
  }
}

export function clearPhoneFormat(formattedPhone: string): string {
  if (formattedPhone)
    return formattedPhone.replace(/\D/g, ''); // Remove non-numeric characters
  else return '';
}

export function trimIfUNE(inputString: string) {
  // Check if the first three letters are "UNE"
  if (inputString.substring(0, 3).toLowerCase() === 'une') {
    // Trim the first three letters and remove extra spaces
    const trimmedString = inputString.substring(3).replace(/\s+/g, '');
    return trimmedString.trim();
  }

  // Return the original string if it doesn't start with "UNE"
  return inputString;
}

export function getQueryParam(query: string, param: string): string | null {
  const regex = new RegExp(`[?&]${param}=([^&]*)`);
  const match = query.match(regex);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Search for a client ID in a list of client IDs (comma-separated string or array).
 * @param clientIds - Either a comma-separated string or an array of client IDs.
 * @param clientIdToSearch - The client ID to search for.
 * @returns True if the client ID is found, otherwise false.
 */
export function searchClientId(
  clientIds: string | string[],
  clientIdToSearch: string,
): boolean {
  if (!clientIds || !clientIdToSearch) {
    return false;
  }

  if (typeof clientIds === 'string') {
    const upperCasedClientIds = clientIds.toUpperCase();
    const upperCasedClientIdToSearch = clientIdToSearch.toUpperCase();

    if (upperCasedClientIds.includes('ALL')) {
      return true;
    }

    // Split the comma-separated string into an array for searching
    return upperCasedClientIds
      .split(',')
      .map((id) => id.trim())
      .includes(upperCasedClientIdToSearch);
  }

  if (Array.isArray(clientIds)) {
    const upperCasedClientIds = clientIds.map((id) => id.toUpperCase());
    const upperCasedClientIdToSearch = clientIdToSearch.toUpperCase();

    if (upperCasedClientIds.includes('ALL')) {
      return true;
    }

    return upperCasedClientIds.includes(upperCasedClientIdToSearch);
  }

  return false;
}

/**
 * Make the form field untouched
 * @returns
 */
export function markFormAsUntouched(form: FormGroup) {
  Object.keys(form.controls).forEach((key) => {
    form.get(key)?.markAsUntouched();
    form.get(key)?.markAsPristine();
  });
}

/**
 * Parse a date string in the format "YYYY-MM-DD" and return a Date object.
 * @param dateStr - The date string to parse.
 * @returns A Date object representing the parsed date.
 */
export function parseLocalDate(
  dateStr: string | undefined | null,
): Date | null {
  if (dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return null;
}

/**
 * Generate an array of numbers between a start and end value, inclusive.
 * @param start - The starting number.  
 * @param end - The ending number.
 * @param step - The step size.
 * @returns An array of numbers.
 */
export const generateRange = (
  start: number,
  end: number,
  step = 1
): number[] =>
  Array.from(
    { length: Math.floor((end - start) / step) + 1 },
    (_, index) => start + index * step
  );
